import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import './MapEditor.scss';
import { Rootstate } from '../../store';
import { setZoom } from '../../reducers/zoom/zoomReducerActions';
import img from '../../examplemap.png';
import Grid, { GRID_SIZE } from './Grid';
import DragManager from './DragManager';
import Loader from '../Loader';
import { preloadImages } from '../../utils/images';
import { setMapImages, setMapLoading, setMapSpells, setTokens } from '../../reducers/map/mapReducerActions';
import Client from '../../utils/network';
import GridRefContext from './GridRefContext';
import { updatePartialMapOnServer } from '../../utils/updatePartialMapOnServer';

export const MapEditor = ({ children, editors, canEdit }: {
    children?: React.ReactNode
    editors: JSX.Element[],
    canEdit: boolean,
}) => {
    const {
        zoom,
        dragging,
        map,
        xTiles,
        yTiles,
        isLoading,
        selectedElement,
        tokens,
        spells,
        images,
        mapId,
    } = useSelector((state: Rootstate) => ({
        zoom: state.zoom.zoom,
        dragging: state.drag.dragging,
        xTiles: state.grid.xTiles,
        yTiles: state.grid.yTiles,
        map: state.map.map?.background,
        isLoading: state.map.imageLoading,
        selectedElement: state.map.selectedElement,
        tokens: state.map.map?.tokenLayer,
        spells: state.map.map?.spellLayer,
        images: state.map.map?.objectLayer,
        mapId: state.map.mapId,
    }), shallowEqual);

    const gridRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (map) {
            preloadImages([`${Client.getBaseUrl()}/api/image/${map}`]).then(() => {
                dispatch(setMapLoading(false));
                return;
            }).catch(() => {
                // do nothing
            });
        }
    }, [map]);

    const dispatch = useDispatch();
    const [scrollPos, setScrollPos] = useState<{ x: number, y: number }>();
    const oldZoom = useRef(zoom);
    const appHolderRef = useRef<HTMLDivElement>(null);
    const appRef = useRef<HTMLDivElement>(null);

    const dragManagerRef = useRef<HTMLDivElement>(null);

    const handleOnWheel = useCallback((e: WheelEvent) => {
        if (!e.altKey) {
            e.preventDefault();
            e.stopPropagation();
            const multiplier = (e.deltaY > 0)
                ? -0.1
                : 0.1;

            const newZoom = Math.min(Math.max(zoom + multiplier, 1), 3);
            dispatch(setZoom(newZoom));
        }
    }, [zoom, dragging, dispatch]);

    const appSize = useMemo(() => {
        return {
            width: GRID_SIZE * xTiles,
            height: GRID_SIZE * yTiles,
        };
    }, [xTiles, yTiles]);

    useEffect(() => {
        if (!appHolderRef.current) {
            return;
        }
        const { width, height } = appHolderRef.current.getBoundingClientRect();
        const { scrollWidth, scrollHeight } = appHolderRef.current;
        const leftScroll = (scrollWidth - width) / 2 ;
        const topScroll = (scrollHeight - height) / 2;
        setScrollPos({ x: leftScroll, y: topScroll / zoom });
    }, []);

    useLayoutEffect(() => {
        const { width = 0 } = appHolderRef.current?.getBoundingClientRect() ?? {};
        const oldX = (appHolderRef.current?.scrollLeft ?? 0) + width / 2;
        const oldY = (appHolderRef.current?.scrollTop ?? 0);
        const zoomRatio = zoom / oldZoom.current;
        const newX = oldX * zoomRatio;
        const newY = oldY * zoomRatio;
        const newScrollX = (appHolderRef.current?.scrollLeft ?? 0) + (newX - oldX);
        const newScrollY = (appHolderRef.current?.scrollTop ?? 0) + (newY - oldY);
        setScrollPos({ x: newScrollX, y: newScrollY });
        oldZoom.current = zoom;
    }, [zoom]);

    useEffect(() => {
        if (scrollPos !== undefined) {
            appHolderRef.current?.scrollTo(scrollPos.x, scrollPos.y);
        }
    }, [scrollPos]);

    const handleDeleteKey = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Delete' && selectedElement) {
            switch (selectedElement.type) {
            case 'token': {
                const newTokens = tokens?.filter((token) => (token._id) !== selectedElement._id) ?? [];
                dispatch(setTokens(newTokens));
                updatePartialMapOnServer({ tokenLayer: newTokens }, mapId);
                break;
            }
            case 'spell': {
                const newSpells = spells?.filter((spell) => (spell._id) !== selectedElement._id) ?? [];
                dispatch(setMapSpells(newSpells));
                updatePartialMapOnServer({ spellLayer: newSpells }, mapId);
                break;
            }
            case 'image': {
                const newMapImages = images?.filter((image) => (image._id) !== selectedElement._id) ?? [];
                dispatch(setMapImages(newMapImages));
                updatePartialMapOnServer({ objectLayer: newMapImages }, mapId);
                break;
            }
            default:
                return;
            }
        }
    }, [selectedElement, tokens, spells, images, mapId]);

    useEffect(() => {
        const doc = document.getElementById('mapeditor-overlay');
        doc?.addEventListener('wheel', handleOnWheel, { passive: false });
        return () => doc?.removeEventListener('wheel', handleOnWheel);
    }, [handleOnWheel, isLoading]);

    useEffect(() => {
        document.addEventListener('keydown', handleDeleteKey, { passive: false });
        return () => document.removeEventListener('keydown', handleDeleteKey);
    }, [handleDeleteKey]);

    const handleMouseDownCapture = (e: React.MouseEvent) => {
        if ((e.ctrlKey && e.button === 0 && e.buttons === 1) || e.button === 2) {
            if(e.button === 2) {
                e.preventDefault();
                e.stopPropagation();
            }
            // e.preventDefault();
            // e.stopPropagation();
            const mouseStartX = e.clientX;
            const mouseStartY = e.clientY;
            const el = document.getElementById('mapeditor-holder') as HTMLDivElement;

            const scrollStartX = el.scrollLeft;
            const scrollStartY = el.scrollTop;

            const handleMouseMovement = (e: MouseEvent) => {
                const dx = e.clientX - mouseStartX;
                const dy = e.clientY - mouseStartY;
                el.scrollLeft = scrollStartX - dx;
                el.scrollTop = scrollStartY - dy;
                el.scroll(scrollStartX - dx, scrollStartY - dy);
            };
            document.body.style.cursor = 'grabbing';

            const handleMouseUp = () => {
                document.body.style.cursor = 'default';
                window.removeEventListener('mouseup', handleMouseUp);
                window.removeEventListener('mousemove', handleMouseMovement);
            };

            window.addEventListener('mousemove', handleMouseMovement);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            const { x = 0, y = 0 } = gridRef.current?.getBoundingClientRect() ?? {};
            const xPos = (((e.clientX - x)) / zoom);
            const yPos = (((e.clientY - y)) / zoom);
            const event = new CustomEvent("custom-move", { detail: {
                x: xPos, y: yPos,
            } });
            document.dispatchEvent(event);

            const handleMouseMovement = (e: MouseEvent) => {
                const xPos = (((e.clientX - x)) / zoom);
                const yPos = (((e.clientY - y)) / zoom);
                const event = new CustomEvent("custom-move", { detail: {
                    x: xPos, y: yPos,
                } });

                document.dispatchEvent(event);
            };

            const handleMouseUp = () => {
                window.removeEventListener('mouseup', handleMouseUp);
                window.removeEventListener('mousemove', handleMouseMovement);
            };
            window.addEventListener('mousemove', handleMouseMovement);
            window.addEventListener('mouseup', handleMouseUp);

        }
    };

    const handleMouseUpCapture = (e: React.MouseEvent) => {
        if (e.button === 1 && e.buttons === 4 || (e.ctrlKey && e.button === 0 && e.buttons === 1)) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    useEffect(() => {
        if(!isLoading) {
            appHolderRef.current?.scrollTo({
                top: (appHolderRef.current?.scrollHeight - appHolderRef.current?.clientHeight) / 2,
                left: (appHolderRef.current?.scrollWidth - appHolderRef.current?.clientWidth) / 2,
            });
        }
    }, [isLoading]);


    return (
        <GridRefContext.Provider value={gridRef}>
            <div
                ref={dragManagerRef}
                id="mapeditor-page"
                className='mapeditor-page bg-back-blue'
                draggable={false}
                onDragStart={() => false}
            >
                <DragManager containerRef={dragManagerRef} />
                {isLoading ? (
                    <div className='m-auto flex grow justify-center align-center bg-back-blue'>
                        <Loader />
                    </div>
                ) : (
                    <div
                        id="mapeditor-overlay"
                        draggable={false}
                        onMouseDownCapture={handleMouseDownCapture}
                        onMouseDown={handleMouseDownCapture}
                        onClickCapture={handleMouseUpCapture}
                        onContextMenu={(e) => e.preventDefault()}
                        onContextMenuCapture={(e) => e.preventDefault()}
                        onDragStart={() => false}
                    >
                        <div
                            id='mapeditor-holder'
                            ref={appHolderRef}
                            className='bg-back-blue pretty-scroll position-relative'
                            draggable={false}
                            onDragStart={() => false}
                        >
                            <div
                                ref={appRef}
                                className="App"
                                id="mapeditor-inner"
                                onDragStart={() => false}
                                draggable={false}
                                style={{
                                    transform: `scale(${zoom})`,
                                    height: `${GRID_SIZE * yTiles}px`,
                                    width: `${GRID_SIZE * xTiles}px`,
                                }}
                            >
                                {map
                                    ? (
                                        <img
                                            alt="background"
                                            className='background'
                                            onDragStart={() => false}
                                            draggable={false}
                                            src={`${Client.getBaseUrl()}/api/image/${map}`}
                                            style={
                                                {
                                                    width: `${GRID_SIZE * xTiles}px`,
                                                    height: `${GRID_SIZE * yTiles}px`,
                                                    userSelect: 'none',
                                                }
                                            }
                                        />
                                    ) : (
                                        <img
                                            alt="background"
                                            className='background'
                                            src={img}
                                            onDragStart={() => false}
                                            style={
                                                { width: `${GRID_SIZE * xTiles}px`, height: `${GRID_SIZE * yTiles}px` }
                                            }
                                        />
                                    )}
                                <Grid canEdit={canEdit} size={appSize} zoom={zoom} gridRef={gridRef} />
                            </div>
                        </div>
                        {editors}
                    </div>
                )}
                <div>
                    {children}
                </div>
            </div>
        </GridRefContext.Provider>
    );
};


export default MapEditor;
